/**
 * Cuts a text answer into speakable pieces while it is still streaming.
 *
 * Waiting for the whole answer before speaking adds its full generation time
 * to the silence. Speaking each finished sentence as it arrives lets Tally
 * start talking after the first one instead.
 *
 * Pieces are cut at line ends and sentence ends only, so the markdown stripping
 * that runs on each piece sees whole lines and whole sentences.
 */
import {
  MAX_SPOKEN_CHARS,
  stripMarkdownForSpeech,
  truncateForSpeech,
} from "./speakableText.ts";

export interface AnswerSpeech {
  /** Characters of the raw answer already handed out. */
  consumed: number;
  /** Characters of speech handed out, against the spoken-length budget. */
  spokenChars: number;
  /** Nothing more will be handed out for this answer. */
  finished: boolean;
}

export function newAnswerSpeech(): AnswerSpeech {
  return { consumed: 0, spokenChars: 0, finished: false };
}

/**
 * A line break, or sentence punctuation followed by whitespace. The character
 * before the punctuation must not be a digit or whitespace, so "1. Revenue"
 * (a list item) and "in 2025. Costs" are not split - the latter just waits for
 * the next boundary.
 */
const BOUNDARY = /\n|[^\d\s][.!?](?=\s)/g;

function lastBoundary(raw: string, from: number): number {
  let end = -1;
  BOUNDARY.lastIndex = from;
  for (let match = BOUNDARY.exec(raw); match; match = BOUNDARY.exec(raw)) {
    end = match.index + match[0].length;
  }
  return end;
}

/**
 * Takes the next speakable piece of `raw`. With `final` set the stream is over
 * and whatever is left is taken.
 */
export function takeSpeakable(
  raw: string,
  state: AnswerSpeech,
  final: boolean,
  truncationSuffix: string,
  maxChars: number = MAX_SPOKEN_CHARS,
): { text: string; state: AnswerSpeech } {
  if (state.finished) return { text: "", state };

  const end = final ? raw.length : lastBoundary(raw, state.consumed);
  if (end <= state.consumed) {
    return { text: "", state: { ...state, finished: final } };
  }

  const piece = stripMarkdownForSpeech(raw.slice(state.consumed, end));
  const next = { ...state, consumed: end, finished: final };
  const budget = maxChars - state.spokenChars;

  if (piece.length <= budget) {
    return {
      text: piece,
      state: { ...next, spokenChars: state.spokenChars + piece.length },
    };
  }

  // Past the budget: end at a sentence boundary that fits and point at the
  // screen, then stop - the rest of the stream is not spoken.
  const { text } = truncateForSpeech(piece, budget);
  return {
    text: text === "" ? truncationSuffix : `${text} ${truncationSuffix}`,
    state: { ...next, spokenChars: maxChars, finished: true },
  };
}
